import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  HiOutlineClipboardCheck, 
  HiOutlineUserGroup, 
  HiOutlineFilter,
  HiOutlineSearch,
  HiPlus,
  HiOutlineExclamationCircle,
  HiX,
  HiOutlineRefresh,
} from 'react-icons/hi';
import { fetchAllTasks, fetchTaskCounts } from '../../../store/slices/taskManagementSlice';
import './AssignTasks.scss';
import TaskCard from './components/TaskCard';
import AssignTaskModal from './components/AssignTaskModal';
import TaskDetailModal from './components/TaskDetailModal';

const AssignTasks = () => {
  const dispatch = useDispatch();
  const employees = useSelector(state => state.auth.employees);
  const { 
    tasks, 
    loading, 
    error 
  } = useSelector(state => state.taskManagement.adminTasks);
  const taskCounts = useSelector(state => state.taskManagement.taskCounts);
  
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0); // Used to trigger refresh

  useEffect(() => {
    dispatch(fetchAllTasks({ 
      statusFilter, 
      employeeFilter, 
      employees 
    }));
    
    dispatch(fetchTaskCounts({ 
      employeeFilter, 
      employees, 
      isAdmin: true 
    }));
  }, [dispatch, statusFilter, employeeFilter, refreshKey, employees]);

  const handleAssignTask = () => {
    setShowAssignModal(true);
  };

  const handleCloseModal = () => {
    setShowAssignModal(false);
  };

  const handleTaskCreated = () => {
    // Refresh the task list after a new task is created
    setRefreshKey(prev => prev + 1);
  };

  const openTaskDetail = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  };

  const closeTaskDetail = () => {
    setShowTaskDetail(false);
    setSelectedTask(null);
    // Refresh task list
    setRefreshKey(prev => prev + 1);
  };

  const handleStatusChange = () => {
    // Refresh task list after status change
    setRefreshKey(prev => prev + 1);
  };

  const filterTasks = () => {
    if (!searchTerm.trim()) {
      return tasks;
    }
    
    const term = searchTerm.toLowerCase();
    return tasks.filter(task => 
      task.title?.toLowerCase().includes(term) || 
      task.description?.toLowerCase().includes(term)
    );
  };

  const filteredTasks = filterTasks();
  
  const getFilteredTasksByStatus = (status) => {
    return filteredTasks.filter(task => task.statusNormalized === status);
  };

  const newTasks = getFilteredTasksByStatus('new');
  const inProgressTasks = getFilteredTasksByStatus('in-progress');
  const completedTasks = getFilteredTasksByStatus('completed');

  return (
    <div className="assign-tasks-container">
      <div className="page-header">
        <div className="header-left">
          <h1>
            <HiOutlineClipboardCheck className="header-icon" />
            Assign Tasks
          </h1>
          <div className="task-counts">
            <div 
              className={`count-badge all ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All Tasks ({taskCounts.all})
            </div>
            <div 
              className={`count-badge new ${statusFilter === 'new' ? 'active' : ''}`}
              onClick={() => setStatusFilter('new')}
            >
              New ({taskCounts.new})
            </div>
            <div 
              className={`count-badge in-progress ${statusFilter === 'in-progress' ? 'active' : ''}`}
              onClick={() => setStatusFilter('in-progress')}
            >
              In Progress ({taskCounts.inProgress})
            </div>
            <div 
              className={`count-badge completed ${statusFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setStatusFilter('completed')}
            >
              Completed ({taskCounts.completed})
            </div>
          </div>
        </div>
        
        <div className="header-right">
          <div className="search-box">
            <HiOutlineSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>
                <HiX />
              </button>
            )}
          </div>
          
          <div className="filter-box">
            <select
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              className="employee-filter"
            >
              <option value="all">All Employees</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
            <HiOutlineFilter className="filter-icon" />
          </div>
          
          <button className="refresh-button" onClick={() => setRefreshKey(prev => prev + 1)}>
            <HiOutlineRefresh />
            <span>Refresh</span>
          </button>
          
          <button className="assign-task-button" onClick={handleAssignTask}>
            <HiPlus />
            <span>Assign Task</span>
          </button>
        </div>
      </div>
      
      <div className="tasks-content">
        {loading && tasks.length === 0 ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading tasks...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <HiOutlineExclamationCircle className="error-icon" />
            <p>{error}</p>
            <button onClick={() => setRefreshKey(prev => prev + 1)}>Try Again</button>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="empty-state">
            <p className="empty-message">
              {searchTerm 
                ? "No tasks match your search criteria." 
                : statusFilter !== 'all' 
                  ? `No ${statusFilter} tasks found.` 
                  : employeeFilter !== 'all'
                    ? "This employee has no assigned tasks."
                    : "No tasks have been assigned yet."}
            </p>
            <button className="create-task-button" onClick={handleAssignTask}>
              <HiPlus />
              Assign New Task
            </button>
          </div>
        ) : (
          <>
            <div className="task-columns">
              {/* New Tasks Column */}
              <div className="task-column new-tasks">
                <h2 className="column-title">
                  New Tasks <span className="count">({newTasks.length})</span>
                </h2>
                <div className="column-content">
                  {newTasks.length === 0 ? (
                    <div className="empty-column">No new tasks</div>
                  ) : (
                    newTasks.map(task => (
                      <TaskCard 
                        key={task.id} 
                        task={task}
                        onClick={() => openTaskDetail(task)}
                        employees={employees}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* In Progress Column */}
              <div className="task-column in-progress-tasks">
                <h2 className="column-title">
                  In Progress <span className="count">({inProgressTasks.length})</span>
                </h2>
                <div className="column-content">
                  {inProgressTasks.length === 0 ? (
                    <div className="empty-column">No in-progress tasks</div>
                  ) : (
                    inProgressTasks.map(task => (
                      <TaskCard 
                        key={task.id} 
                        task={task}
                        onClick={() => openTaskDetail(task)}
                        employees={employees}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Completed Column */}
              <div className="task-column completed-tasks">
                <h2 className="column-title">
                  Completed <span className="count">({completedTasks.length})</span>
                </h2>
                <div className="column-content">
                  {completedTasks.length === 0 ? (
                    <div className="empty-column">No completed tasks</div>
                  ) : (
                    completedTasks.map(task => (
                      <TaskCard 
                        key={task.id} 
                        task={task}
                        onClick={() => openTaskDetail(task)}
                        employees={employees}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      <AssignTaskModal
        show={showAssignModal}
        onClose={handleCloseModal}
        onTaskCreated={handleTaskCreated}
        employees={employees}
      />
      
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={closeTaskDetail}
          onStatusChange={handleStatusChange}
          employees={employees}
        />
      )}
    </div>
  );
};

export default AssignTasks; 